import { useState, useEffect, useCallback, useMemo } from 'react';
import { Toaster, toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';

import { Header } from './components/Header/Header';
import { SearchAndFilters } from './components/Filters/SearchAndFilters';
import { FoodTruckMap } from './components/Map/FoodTruckMap';
import { TimelineSlider } from './components/Timeline/TimelineSlider';
import { Sidebar } from './components/Sidebar/Sidebar';
import { MobileBottomNav } from './components/Mobile/MobileBottomNav';
import { ListView } from './components/ListView/ListView';
import { EventDetailModal } from './components/Events/EventDetailModal';
import { PWAInstallPrompt } from './components/Mobile/PWAInstallPrompt';
import { usePWAInstall } from './hooks/usePWAInstall';
import { getEventsWithCurrentStatus, getNextEvent, locations } from './data/events';

function App() {
  // Get events with current status calculated from real date/time
  const [events, setEvents] = useState(() => getEventsWithCurrentStatus());
  
  // Get the next upcoming event or first event as default
  const initialEvent = getNextEvent();
  const initialIndex = events.findIndex(e => e.id === initialEvent.id);
  
  const [currentEventIndex, setCurrentEventIndex] = useState(initialIndex >= 0 ? initialIndex : 0);
  const [selectedLocation, setSelectedLocation] = useState('Tous');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedEventForModal, setSelectedEventForModal] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  const currentEvent = events[currentEventIndex];
  const { showPrompt, installApp, dismissPrompt, isIOS } = usePWAInstall();
  
  // Filter events by location and search query
  const filteredEvents = useMemo(() => {
    // Normalize string for search (remove accents)
    const normalizeString = (str) => {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    };
    
    const normalizedSearch = normalizeString(searchQuery);
    
    return events.filter(event => {
      const matchesLocation = selectedLocation === 'Tous' || event.location === selectedLocation;
      const matchesSearch = searchQuery === '' || 
        normalizeString(event.name).includes(normalizedSearch) ||
        normalizeString(event.location).includes(normalizedSearch) ||
        normalizeString(event.description).includes(normalizedSearch);
      return matchesLocation && matchesSearch;
    });
  }, [events, selectedLocation, searchQuery]);

  // Hide Emergent badge
  useEffect(() => {
    const hideEmergentBadge = () => {
      // Target the specific Emergent badge by looking for fixed positioned elements at bottom
      const elements = document.querySelectorAll('div[style*="position: fixed"], button[style*="position: fixed"]');
      elements.forEach(el => {
        const style = window.getComputedStyle(el);
        const text = el.textContent || '';
        // Only hide if it's at the bottom and contains "Emergent"
        if (style.position === 'fixed' && 
            (style.bottom === '0px' || parseInt(style.bottom) < 50) && 
            text.includes('Emergent')) {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    };

    // Run after a delay to let the badge render
    const timeout = setTimeout(hideEmergentBadge, 1000);
    const interval = setInterval(hideEmergentBadge, 2000);
    
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  // Update events status every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setEvents(getEventsWithCurrentStatus());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Request geolocation
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => {
          // User denied or error - silently fail
        }
      );
    }
  }, []);

  // Find nearest event to user
  const findNearestEvent = useCallback(() => {
    if (!userLocation) {
      toast.error('Activez la géolocalisation pour cette fonctionnalité');
      return;
    }

    const upcomingEvents = events.filter(e => e.status !== 'past');
    if (upcomingEvents.length === 0) return;

    let nearest = upcomingEvents[0];
    let minDistance = Infinity;

    upcomingEvents.forEach(event => {
      const distance = Math.sqrt(
        Math.pow(event.coordinates.lat - userLocation.lat, 2) +
        Math.pow(event.coordinates.lng - userLocation.lng, 2)
      );
      if (distance < minDistance) {
        minDistance = distance;
        nearest = event;
      }
    });

    const eventIndex = events.findIndex(e => e.id === nearest.id);
    if (eventIndex >= 0) {
      setCurrentEventIndex(eventIndex);
      toast.success(`Événement le plus proche: ${nearest.name}`, {
        description: nearest.location
      });
    }
  }, [userLocation, events]);

  // Handle location filter change
  const handleLocationChange = useCallback((location) => {
    setSelectedLocation(location);
    
    if (location !== 'Tous') {
      const firstEventInLocation = events.findIndex(e => e.location === location);
      if (firstEventInLocation >= 0) {
        setCurrentEventIndex(firstEventInLocation);
      }
    }
  }, [events]);

  // Handle event selection
  const handleEventChange = useCallback((index) => {
    setCurrentEventIndex(index);
  }, []);

  // Handle swipe for mobile
  const handleSwipe = useCallback((direction) => {
    if (direction === 'left' && currentEventIndex < events.length - 1) {
      setCurrentEventIndex(prev => prev + 1);
    } else if (direction === 'right' && currentEventIndex > 0) {
      setCurrentEventIndex(prev => prev - 1);
    }
  }, [currentEventIndex, events.length]);

  // Show welcome notification on first load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
      
      const nextEvent = getNextEvent();
      if (nextEvent) {
        const statusText = nextEvent.status === 'active' ? 'En cours' : 'Prochain arrêt';
        toast.info(`${statusText}: ${nextEvent.name}`, {
          description: `${nextEvent.displayDates} à ${nextEvent.location}`,
          duration: 5000,
          icon: '🚚'
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div data-testid="app-container" className="relative w-full h-screen overflow-hidden bg-white">
      <Toaster 
        position="bottom-center"
        richColors 
        toastOptions={{
          style: {
            fontFamily: "'DM Sans', sans-serif"
          },
          className: 'mb-36 md:mb-44'
        }}
      />
      
      {/* Loading overlay */}
      <AnimatePresence>
        {!isLoaded && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[100] bg-white flex items-center justify-center"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{ 
                duration: 1.5, 
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="text-center"
            >
              <div className="w-16 h-16 border-4 border-[#97c7c3] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="font-league text-xl uppercase tracking-wide text-black">
                Chargement...
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header - Mobile only */}
      <Header />

      {/* Search and Filters */}
      <SearchAndFilters 
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedLocation={selectedLocation}
        onLocationChange={handleLocationChange}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        locations={locations}
      />

      {/* Map or List View */}
      <AnimatePresence mode="wait">
        {viewMode === 'map' ? (
          <motion.div 
            key="map"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-0"
          >
            <FoodTruckMap
              currentEvent={currentEvent}
              filteredEvents={filteredEvents}
              allEvents={events}
              onEventSelect={handleEventChange}
              selectedLocation={selectedLocation}
              onEventClick={(event) => setSelectedEventForModal(event)}
            />
          </motion.div>
        ) : (
          <ListView
            key="list"
            events={filteredEvents}
            currentEvent={currentEvent}
            onEventSelect={handleEventChange}
            allEvents={events}
          />
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      {viewMode === 'map' && (
        <Sidebar
          currentEvent={currentEvent}
          onEventSelect={handleEventChange}
          filteredEvents={filteredEvents}
          allEvents={events}
        />
      )}

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        currentEvent={currentEvent}
        currentEventIndex={currentEventIndex}
        totalEvents={events.length}
        onPrevEvent={() => handleSwipe('right')}
        onNextEvent={() => handleSwipe('left')}
        onEventClick={() => setSelectedEventForModal(currentEvent)}
        onFindNearest={findNearestEvent}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Timeline Slider - Desktop and tablet */}
      <div className="hidden md:block">
        <TimelineSlider
          currentEventIndex={currentEventIndex}
          onEventChange={handleEventChange}
          selectedEvent={currentEvent}
          allEvents={events}
        />
      </div>

      {/* Event Detail Modal - Mobile */}
      <EventDetailModal
        event={selectedEventForModal}
        isOpen={!!selectedEventForModal}
        onClose={() => setSelectedEventForModal(null)}
      />

      {/* PWA Install Prompt */}
      <PWAInstallPrompt
        show={showPrompt}
        onInstall={installApp}
        onDismiss={dismissPrompt}
        isIOS={isIOS}
      />

      {/* Empty state for no results */}
      {filteredEvents.length === 0 && viewMode === 'map' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none"
        >
          <div className="bg-white/95 backdrop-blur-lg rounded-2xl p-8 shadow-xl text-center max-w-sm mx-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🔍</span>
            </div>
            <h3 className="font-league text-xl uppercase tracking-wide text-black mb-2">
              Aucun résultat
            </h3>
            <p className="font-dm text-sm text-gray-500">
              Essayez de modifier vos filtres ou votre recherche
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedLocation('Tous');
              }}
              className="mt-4 px-4 py-2 bg-[#97c7c3] text-black rounded-full font-dm text-sm font-medium pointer-events-auto"
            >
              Réinitialiser les filtres
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default App;
